import { IntlShape } from 'react-intl';
import { platformMeta } from '../platformMeta';
import type { CalendarPost, CalendarSong } from '../types';
import { calendarStateOf } from './calendarFilterModel';
import { calendarMessages, calendarStateMessages } from './calendarMessages';
import { formatPostTime, postTitle } from './calendarPostView';

/** The state word of a post. A post that goes out now says so. */
export function postStateWord(
  intl: IntlShape,
  post: Pick<CalendarPost, 'status'> & { needsSettings?: boolean }
): string {
  if (post.status === 'publishing') {
    return intl.formatMessage(calendarMessages.publishing);
  }
  // A draft that waits for the user's platform settings (TikTok Direct Post G10).
  if (post.needsSettings === true && post.status === 'draft') {
    return intl.formatMessage(calendarMessages.needsSettings);
  }
  return intl.formatMessage(calendarStateMessages[calendarStateOf(post.status)]);
}

/** The time of a post, `13:00`. */
export function postTime(intl: IntlShape, post: Pick<CalendarPost, 'scheduledAt'>): string {
  return formatPostTime(intl.formatTime, new Date(post.scheduledAt));
}

/**
 * What a screen reader reads for a card (design b10): "Instagram, 12:00, Swamps, story, needs you".
 * The state word is in the name, so the mark is never the only sign.
 */
export function postCardLabel(intl: IntlShape, post: CalendarPost, song: CalendarSong | undefined): string {
  return intl.formatMessage(calendarMessages.cardLabel, {
    platform: platformMeta[post.platform].name,
    time: postTime(intl, post),
    title: postTitle(post, song),
    format: intl.formatMessage(post.format === 'story' ? calendarMessages.story : calendarMessages.post),
    state: postStateWord(intl, post),
  });
}

/** "Thursday 29 October, 18:00", for the live region of a move. */
export function formatMoveTime(intl: IntlShape, at: Date): string {
  return `${intl.formatDate(at, { weekday: 'long', day: 'numeric', month: 'long' })}, ${formatPostTime(
    intl.formatTime,
    at
  )}`;
}
